/**
 * Utility functions for Apollo Cache management
 * Handles cache invalidation when reservations are updated
 *
 * 🎯 PERFORMANCE OPTIMIZATION: Added progressive loading utilities
 */
import { logger } from './logger';

/**
 * Invalidate job and reservation related cache entries
 * Call this after any reservation update mutation
 */
export const invalidateJobAndReservationCache = (client, propertyId, date) => {
  if (!client) return;

  try {
    // Invalidate next reservation cache for the property
    if (propertyId) {
      client.cache.evict({
        id: `Query`,
        fieldName: 'nextReservationForProperty',
        args: {
          propertyId: propertyId,
          date: date
        }
      });

      // Also invalidate without specific date to catch all entries
      client.cache.evict({
        id: `Query`,
        fieldName: 'nextReservationForProperty',
        args: {
          propertyId: propertyId
        }
      });
    }

    // Invalidate previous reservation cache
    if (propertyId) {
      client.cache.evict({
        id: `Query`,
        fieldName: 'previousReservationForProperty',
        args: {
          propertyId: propertyId,
          date: date
        }
      });

      // Also invalidate without specific date
      client.cache.evict({
        id: `Query`,
        fieldName: 'previousReservationForProperty',
        args: {
          propertyId: propertyId
        }
      });
    }

    // Invalidate jobs cache that might contain embedded reservation data
    client.cache.evict({
      id: `Query`,
      fieldName: 'jobs'
    });

    // 🎯 CRITICAL: Clear MySchedule cache to fix reservation update refresh issue
    client.cache.evict({
      id: `Query`,
      fieldName: 'myScheduleJobs'
    });

    // Invalidate property reservations cache
    client.cache.evict({
      id: `Query`,
      fieldName: 'propertyReservations'
    });

    // Invalidate general reservations cache
    client.cache.evict({
      id: `Query`,
      fieldName: 'reservations'
    });

    // Run garbage collection to clean up orphaned references
    client.cache.gc();

    logger.debug('Cache invalidated for reservation updates', { propertyId, date });
  } catch (error) {
    logger.error('Error invalidating cache', error);
  }
};

/**
 * Invalidate cache for a specific job
 * Call this when a job's reservation data is updated
 */
export const invalidateJobCache = (client, jobId) => {
  if (!client || !jobId) return;

  try {
    // Invalidate the specific job
    client.cache.evict({
      id: `Job:${jobId}`
    });

    // Invalidate jobs queries that might contain this job
    client.cache.evict({
      id: `Query`,
      fieldName: 'jobs'
    });

    client.cache.evict({
      id: `Query`,
      fieldName: 'jobsForAssignments'
    });

    // 🎯 CRITICAL: Clear MySchedule cache to fix job update refresh issue
    client.cache.evict({
      id: `Query`,
      fieldName: 'myScheduleJobs'
    });

    // Run garbage collection
    client.cache.gc();

    logger.debug('Cache invalidated for job update', { jobId });
  } catch (error) {
    logger.error('Error invalidating job cache', error);
  }
};

/**
 * Force refetch of job-related queries
 * Use this for critical real-time updates
 */
export const refetchJobQueries = async (client, variables = {}) => {
  if (!client) return;

  try {
    // Refetch jobs query with current variables
    await client.refetchQueries({
      include: ['GetJobs', 'GetJobsForAssignments'],
      variables
    });

    logger.debug('Job queries refetched', variables);
  } catch (error) {
    logger.error('Error refetching job queries', error);
  }
};

/**
 * Clear all reservation-related cache entries
 * Use this as a nuclear option when other methods fail
 */
export const clearReservationCache = (client) => {
  if (!client) return;

  try {
    // Clear all reservation-related queries
    const reservationQueries = [
      'nextReservationForProperty',
      'previousReservationForProperty',
      'propertyReservations',
      'reservations',
      'upcomingReservations',
      'searchReservations'
    ];

    reservationQueries.forEach(queryName => {
      client.cache.evict({
        id: `Query`,
        fieldName: queryName
      });
    });

    // Clear job queries as they contain embedded reservation data
    client.cache.evict({
      id: `Query`,
      fieldName: 'jobs'
    });

    client.cache.evict({
      id: `Query`,
      fieldName: 'jobsForAssignments'
    });

    // Run garbage collection
    client.cache.gc();

    logger.debug('All reservation cache cleared');
  } catch (error) {
    logger.error('Error clearing reservation cache', error);
  }
};

/**
 * Invalidate employee-specific job cache entries
 * Call this when jobs are assigned/unassigned to employees
 */
export const invalidateEmployeeJobsCache = (client, employeeId, dateRange = null) => {
  if (!client) return;

  try {
    // Invalidate employee-specific assigned jobs cache
    client.cache.evict({
      id: `Query`,
      fieldName: 'employeeAssignedJobs',
      args: {
        input: {
          employeeId: employeeId,
          ...(dateRange || {})
        }
      }
    });

    // Invalidate all employee assigned jobs queries for this employee
    client.cache.evict({
      id: `Query`,
      fieldName: 'employeeAssignedJobs',
      args: {
        input: {
          employeeId: employeeId
        }
      }
    });

    // Invalidate the employees summary cache
    client.cache.evict({
      id: `Query`,
      fieldName: 'assignedEmployeesSummary'
    });

    // Run garbage collection
    client.cache.gc();

    logger.debug('Employee job cache invalidated', { employeeId, dateRange });
  } catch (error) {
    logger.error('Error invalidating employee job cache', error);
  }
};

/**
 * Clear all employee-specific cache entries
 * Use this when major assignment changes occur
 */
export const clearEmployeeAssignmentCache = (client) => {
  if (!client) return;

  try {
    // Clear all employee-related queries
    const employeeQueries = [
      'assignedEmployeesSummary',
      'employeeAssignedJobs'
    ];

    employeeQueries.forEach(queryName => {
      client.cache.evict({
        id: `Query`,
        fieldName: queryName
      });
    });

    // Also clear main assignment queries
    client.cache.evict({
      id: `Query`,
      fieldName: 'jobsForAssignments'
    });

    client.cache.evict({
      id: `Query`,
      fieldName: 'assignedJobsForAssignments'
    });

    // Run garbage collection
    client.cache.gc();

    logger.debug('All employee assignment cache cleared');
  } catch (error) {
    logger.error('Error clearing employee assignment cache', error);
  }
};

/**
 * Invalidate all assignment-related cache entries
 * Call this when date changes or major assignment updates occur
 */
export const invalidateAssignmentCaches = (client) => {
  if (!client) return;

  try {
    // Clear all assignment-related caches
    client.cache.evict({ fieldName: 'jobsForAssignments' });
    client.cache.evict({ fieldName: 'assignedJobsForAssignments' });
    client.cache.evict({ fieldName: 'jobs' });

    // 🎯 CRITICAL: Clear MySchedule cache to fix job assignment refresh issue
    client.cache.evict({ fieldName: 'myScheduleJobs' });

    // Clear optimized assignment caches
    client.cache.evict({ fieldName: 'assignedEmployeesSummary' });
    client.cache.evict({ fieldName: 'employeeAssignedJobs' });

    // Clear any cached eligible users data
    client.cache.evict({ fieldName: 'eligibleUsersForJobs' });

    // Clear any cached teams data that might be stale
    client.cache.evict({ fieldName: 'activeTeams' });

    // Run garbage collection to clean up orphaned references
    client.cache.gc();

    logger.debug('All assignment caches invalidated');
  } catch (error) {
    logger.error('Error invalidating assignment caches', error);
  }
};

/**
 * Invalidate assignment caches for date changes
 * Call this specifically when cycling through dates
 */
export const invalidateAssignmentCachesForDateChange = (client, newDate) => {
  if (!client) return;

  try {
    // Clear all assignment-related caches
    invalidateAssignmentCaches(client);

    // 🎯 ENHANCED: More thorough cache clearing for date changes
    // Clear all query caches by specifying Query id explicitly
    client.cache.evict({
      id: 'Query',
      fieldName: 'jobsByDate'
    });
    client.cache.evict({
      id: 'Query',
      fieldName: 'scheduleJobsOptimized'
    });

    // 🎯 NEW: Clear additional caches that might contain date-specific data
    client.cache.evict({
      id: 'Query',
      fieldName: 'myScheduleJobs'
    });

    // Clear any cached reservation data that might affect job display
    client.cache.evict({
      id: 'Query',
      fieldName: 'nextReservationForProperty'
    });
    client.cache.evict({
      id: 'Query',
      fieldName: 'previousReservationForProperty'
    });

    // 🎯 CRITICAL: Clear the entire cache for date changes to prevent stale data
    // This is more aggressive but ensures no stale data remains
    if (client.cache.reset) {
      // Only reset if the method exists (some Apollo Client versions)
      logger.debug('Performing full cache reset for date change');
      client.cache.reset();
    } else {
      // Fallback: Run multiple garbage collections
      client.cache.gc();
      client.cache.gc(); // Run twice to ensure cleanup
    }

    logger.debug('Assignment caches invalidated for date change', { newDate });
  } catch (error) {
    logger.error('Error invalidating assignment caches for date change', error);
  }
};

/**
 * Hook to provide cache invalidation utilities
 * Use this in components that update reservations
 */
export const useCacheInvalidation = (client) => {
  return {
    invalidateJobAndReservationCache: (propertyId, date) =>
      invalidateJobAndReservationCache(client, propertyId, date),
    invalidateJobCache: (jobId) =>
      invalidateJobCache(client, jobId),
    refetchJobQueries: (variables) =>
      refetchJobQueries(client, variables),
    clearReservationCache: () =>
      clearReservationCache(client),
    invalidateEmployeeJobsCache: (employeeId, dateRange) =>
      invalidateEmployeeJobsCache(client, employeeId, dateRange),
    clearEmployeeAssignmentCache: () =>
      clearEmployeeAssignmentCache(client),
    invalidateAssignmentCaches: () =>
      invalidateAssignmentCaches(client),
    invalidateAssignmentCachesForDateChange: (newDate) =>
      invalidateAssignmentCachesForDateChange(client, newDate)
  };
};

/**
 * 🎯 PERFORMANCE OPTIMIZATION: Progressive loading utilities
 */

/**
 * Check if a query should use progressive loading based on data size
 * @param {number} totalItems - Total number of items available
 * @param {number} currentItems - Current number of items loaded
 * @param {number} threshold - Threshold for progressive loading (default: 50)
 * @returns {boolean} - Whether to use progressive loading
 */
export const shouldUseProgressiveLoading = (totalItems, currentItems, threshold = 50) => {
  return totalItems > threshold && currentItems < totalItems;
};

/**
 * Calculate optimal batch size for progressive loading
 * @param {number} totalItems - Total number of items
 * @param {number} currentItems - Current number of items loaded
 * @param {number} baseBatchSize - Base batch size (default: 15)
 * @returns {number} - Optimal batch size
 */
export const calculateOptimalBatchSize = (totalItems, currentItems, baseBatchSize = 15) => {
  const remaining = totalItems - currentItems;

  // If remaining items are small, load them all
  if (remaining <= baseBatchSize * 2) {
    return remaining;
  }

  // Otherwise use base batch size
  return baseBatchSize;
};

/**
 * Create a progressive loading queue for large datasets
 * @param {number} totalItems - Total number of items
 * @param {number} currentItems - Current number of items loaded
 * @param {number} batchSize - Size of each batch
 * @returns {Array} - Array of batch configurations
 */
export const createProgressiveLoadQueue = (totalItems, currentItems, batchSize = 15) => {
  const queue = [];
  let offset = currentItems;

  while (offset < totalItems) {
    const remaining = totalItems - offset;
    const currentBatchSize = Math.min(batchSize, remaining);

    queue.push({
      offset,
      limit: currentBatchSize,
      priority: queue.length // Lower number = higher priority
    });

    offset += currentBatchSize;
  }

  return queue;
};

/**
 * Debounce function for progressive loading to prevent rapid-fire requests
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function with cleanup
 */
export const debounceProgressiveLoad = (func, wait = 1000) => {
  let timeout;
  const debouncedFunction = function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };

  // Add cleanup method to the debounced function
  debouncedFunction.cancel = () => {
    clearTimeout(timeout);
  };

  return debouncedFunction;
};
